/**
 * ServiceTechnologiesSection — the "Technologies" section of the Service
 * Detail page: assign form (search + optional version pin) and the assigned
 * list with remove actions. Extracted from `routes/services/$serviceId.tsx`
 * (issue #1411).
 */
import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useTechnologies, useTechnologyVersions } from '../../graphql/queries';
import { PermissionGate, StatusBadge } from '../shared';
import {
  assignTechnologyToService,
  removeTechnologyFromService,
  ApiError,
} from '../../lib/apiClient';
import { TechnologyVersionStatus } from '../../graphql/types';
import type { ServiceTechnologyAssignment } from '../../graphql/types';

const BannerElement = 'forge-banner' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

export interface ServiceTechnologiesSectionProps {
  serviceId: string;
  assignedTechnologyAssignments: ServiceTechnologyAssignment[];
}

/** Technology assignment form + list for the Service Detail page. */
export function ServiceTechnologiesSection({
  serviceId,
  assignedTechnologyAssignments,
}: ServiceTechnologiesSectionProps): JSX.Element {
  const apolloClient = useApolloClient();

  const [showTechAssignForm, setShowTechAssignForm] = useState(false);
  const [techSearch, setTechSearch] = useState('');
  // Debounced copy of `techSearch` (issue #1348) — drives the server-side
  // `filter.search` query below so the picker isn't limited to whatever
  // lands in the first 500-row scan-order slice. 300ms matches the global
  // SearchBar's debounce (frontend/src/components/layouts/SearchBar.tsx).
  const [debouncedTechSearch, setDebouncedTechSearch] = useState('');
  const [selectedTechId, setSelectedTechId] = useState('');
  const [techNotes, setTechNotes] = useState('');
  // Optional published-version pin (issue #1284) — reset whenever the
  // selected technology changes, since a version only belongs to one
  // technology.
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [techAssignError, setTechAssignError] = useState<string | null>(null);
  const [isAssigningTech, setIsAssigningTech] = useState(false);
  const [removingTechId, setRemovingTechId] = useState<string | null>(null);

  // Debounce the picker's search text before sending it to the server
  // (issue #1348) — avoids a network round trip on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTechSearch(techSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [techSearch]);

  // Technology data for the picker. Routed through the server's
  // `filter.search` (name + description + version, issue #765) instead of
  // an unfiltered `first: 500` scan-order slice — with 1000+ catalog
  // entities, an unfiltered slice never reaches bulk-imported technologies
  // that land past row 500, so no client-side re-filter could ever find
  // them (issue #1348).
  const { data: allTechData } = useTechnologies(
    debouncedTechSearch ? { search: debouncedTechSearch } : undefined,
    500
  );
  const allTechnologies = allTechData?.technologies?.edges?.map((e) => e.node) ?? [];

  // Published versions of the selected technology (issue #1284) — powers
  // the optional version pin. Filtered server-side to `status: published`
  // (from #1283); skipped entirely until a technology is selected.
  const { data: selectedTechVersionsData } = useTechnologyVersions(
    selectedTechId,
    TechnologyVersionStatus.Published
  );
  const selectedTechPublishedVersions = selectedTechVersionsData?.technologyVersions ?? [];

  const assignedTechIds = new Set(assignedTechnologyAssignments.map((a) => a.technologyId));

  // Picker list, excluding already-assigned technologies. Search matching
  // itself now happens server-side via `filter.search` above (issue #1348).
  const filteredPickerTechs = allTechnologies.filter((t) => !assignedTechIds.has(t.entityId));

  return (
    <section
      className="service-technologies-section service-section"
      aria-labelledby="service-technologies-heading"
      data-testid="service-technologies-section"
    >
      <div className="service-section-header">
        <h2 id="service-technologies-heading" className="forge-typography--headline6">
          Technologies
        </h2>
        <PermissionGate permission="service:update">
          {!showTechAssignForm && (
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={() => {
                setShowTechAssignForm(true);
                setTechAssignError(null);
                setTechSearch('');
                setSelectedTechId('');
                setTechNotes('');
                setSelectedVersionId('');
              }}
            >
              Assign Technology
            </ForgeButton>
          )}
        </PermissionGate>
      </div>

      {techAssignError && (
        <BannerElement
          theme="error"
          dismissed={false}
          role="alert"
          className="assignment-banner"
          data-testid="tech-assign-error"
        >
          <span>{techAssignError}</span>
        </BannerElement>
      )}

      {showTechAssignForm && (
        <div className="tech-assign-form" data-testid="tech-assign-form">
          <div className="tech-assign-field">
            <label htmlFor="tech-search" className="forge-typography--caption">
              Search technologies
            </label>
            <input
              id="tech-search"
              type="text"
              className="filter-select"
              placeholder="Filter by name or vendor…"
              value={techSearch}
              onChange={(e) => {
                setTechSearch(e.target.value);
                setSelectedTechId('');
              }}
              aria-label="Search technologies"
            />
          </div>

          <div className="tech-assign-field">
            <label htmlFor="tech-picker" className="forge-typography--caption">
              Technology
            </label>
            <select
              id="tech-picker"
              className="filter-select"
              value={selectedTechId}
              onChange={(e) => {
                setSelectedTechId(e.target.value);
                // A version only belongs to one technology — clear any
                // stale pin when the technology selection changes
                // (issue #1284).
                setSelectedVersionId('');
              }}
              aria-label="Select technology"
              data-testid="tech-picker-select"
            >
              <option value="">— Select a technology —</option>
              {filteredPickerTechs.map((t) => (
                <option key={t.entityId} value={t.entityId}>
                  {t.name}
                  {t.lifecycleStage ? ` (${t.lifecycleStage})` : ''}
                </option>
              ))}
            </select>
            {filteredPickerTechs.length === 0 && debouncedTechSearch && (
              <p className="forge-typography--caption page-header-subtitle">
                No technologies match your search.
              </p>
            )}
          </div>

          {/*
            Optional published-version pin (issue #1284). Disabled until
            a technology is selected — a version only makes sense
            relative to its parent technology.
          */}
          <div className="tech-assign-field">
            <label htmlFor="tech-version-picker" className="forge-typography--caption">
              Version (optional)
            </label>
            <select
              id="tech-version-picker"
              className="filter-select"
              value={selectedVersionId}
              onChange={(e) => setSelectedVersionId(e.target.value)}
              disabled={!selectedTechId}
              aria-label="Select published version"
              data-testid="tech-version-picker-select"
            >
              <option value="">— No specific version —</option>
              {selectedTechPublishedVersions.map((v) => (
                <option key={v.entityId} value={v.entityId}>
                  {v.versionLabel}
                </option>
              ))}
            </select>
            {selectedTechId && selectedTechPublishedVersions.length === 0 && (
              <p className="forge-typography--caption page-header-subtitle">
                No published versions for this technology yet.
              </p>
            )}
          </div>

          <div className="tech-assign-field">
            <label htmlFor="tech-notes" className="forge-typography--caption">
              Notes (optional)
            </label>
            <textarea
              id="tech-notes"
              className="filter-select tech-assign-notes"
              placeholder="Why / how this technology is used…"
              value={techNotes}
              onChange={(e) => setTechNotes(e.target.value)}
              aria-label="Assignment notes"
              data-testid="tech-notes-textarea"
              rows={2}
            />
          </div>

          <div className="tech-assign-actions">
            <ForgeButton
              type="button"
              variant="raised"
              disabled={isAssigningTech || !selectedTechId}
              onClick={() => {
                if (!selectedTechId) return;
                setIsAssigningTech(true);
                setTechAssignError(null);
                assignTechnologyToService(serviceId, {
                  technologyId: selectedTechId,
                  notes: techNotes || undefined,
                  versionId: selectedVersionId || undefined,
                })
                  .then(() => {
                    setShowTechAssignForm(false);
                    setSelectedTechId('');
                    setTechSearch('');
                    setTechNotes('');
                    setSelectedVersionId('');
                    // Refetch service to update technologies list
                    void apolloClient.refetchQueries({ include: ['GetService'] });
                  })
                  .catch((err) => {
                    setTechAssignError(
                      err instanceof ApiError ? err.message : 'Failed to assign technology'
                    );
                  })
                  .finally(() => setIsAssigningTech(false));
              }}
            >
              Assign
            </ForgeButton>
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={() => setShowTechAssignForm(false)}
              data-testid="tech-assign-cancel"
            >
              Cancel
            </ForgeButton>
          </div>
        </div>
      )}

      {assignedTechnologyAssignments.length === 0 && !showTechAssignForm && (
        <p className="forge-typography--body2 page-header-subtitle">
          No technologies assigned yet.
        </p>
      )}

      {assignedTechnologyAssignments.map((assignment) => {
        const tech = assignment.technology;
        const pinnedVersion = assignment.metadata.version;
        return (
          <div
            key={assignment.technologyId}
            className="tech-row"
            data-testid={`tech-row-${assignment.technologyId}`}
          >
            <div className="tech-row-info">
              <span className="forge-typography--body2">
                {tech?.name ?? assignment.technologyId}
              </span>
              {tech?.lifecycleStage && (
                <StatusBadge variant="lifecycleStage" value={tech.lifecycleStage} />
              )}
              {pinnedVersion && (
                <span
                  className="tech-row-version"
                  data-testid={`tech-row-version-${assignment.technologyId}`}
                >
                  v{pinnedVersion.versionLabel}
                </span>
              )}
            </div>
            <PermissionGate permission="service:update">
              <ForgeButton
                type="button"
                variant="text"
                disabled={removingTechId === assignment.technologyId}
                onClick={() => {
                  setRemovingTechId(assignment.technologyId);
                  removeTechnologyFromService(serviceId, assignment.technologyId)
                    .then(() => {
                      void apolloClient.refetchQueries({ include: ['GetService'] });
                    })
                    .catch((err) => {
                      setTechAssignError(
                        err instanceof ApiError ? err.message : 'Failed to remove technology'
                      );
                    })
                    .finally(() => setRemovingTechId(null));
                }}
                aria-label={`Remove ${tech?.name ?? assignment.technologyId}`}
                data-testid={`remove-tech-${assignment.technologyId}`}
              >
                <forge-icon name="close" slot="start" aria-hidden="true"></forge-icon>
                Remove
              </ForgeButton>
            </PermissionGate>
          </div>
        );
      })}
    </section>
  );
}
